/**
 * Mirror of the server's permission catalog (`src/config/permissions.ts` in the
 * API). The server is the authority — this list exists so the UI can hide what
 * a user cannot do, and so permission names are typed instead of stringly
 * scattered through components.
 *
 * When a permission is added on the server, add it here too, then run
 * `npm run sync:permissions` on the API so existing databases pick it up.
 */

export const PERMISSION_GROUPS: Record<string, string[]> = {
    user: ['create', 'read', 'update', 'delete', 'list', 'profile', 'changePassword', 'assignRole', 'revokeRole'],
    role: ['create', 'read', 'update', 'delete', 'list', 'assign', 'revoke'],
    permission: ['read', 'list', 'create'],

    customer: ['create', 'read', 'update', 'delete', 'list', 'balance', 'history'],
    supplier: ['create', 'read', 'update', 'delete', 'list', 'balance', 'history'],
    product: ['create', 'read', 'update', 'delete', 'list', 'stock', 'pricing', 'resetStock', 'restoreStock', 'recalculateStock'],
    inventory: ['view', 'adjust', 'transfer', 'count', 'report'],
    currency: ['create', 'read', 'update', 'delete', 'list', 'rate'],
    city: ['create', 'read', 'update', 'delete', 'list'],

    sell: ['create', 'read', 'update', 'delete', 'list', 'invoice', 'receipt', 'report'],
    sellInvoice: ['create', 'read', 'update', 'delete', 'list', 'print', 'email', 'void'],

    purchase: ['create', 'read', 'update', 'delete', 'list', 'receive', 'approve', 'report'],
    purchaseInvoice: ['create', 'read', 'update', 'delete', 'list', 'print', 'approve'],

    returnInvoice: ['create', 'read', 'update', 'delete', 'list', 'print', 'approve', 'process', 'report'],

    expense: ['create', 'read', 'update', 'delete', 'list', 'approve', 'category', 'report'],
    supplierPayment: ['create', 'read', 'update', 'delete', 'list', 'approve', 'report'],
    customerPayment: ['create', 'read', 'update', 'delete', 'list', 'process'],
    loanRepayment: ['create', 'read', 'update', 'delete', 'list', 'approve', 'report'],
    loan: ['create', 'read', 'update', 'delete', 'approve', 'disburse'],

    order: ['create', 'read', 'update', 'delete', 'list', 'count', 'nextStatus', 'prevStatus', 'changeStatus', 'cancel', 'fulfill', 'report'],

    report: [
        'dashboard', 'read', 'export',
        'profit', 'loss', 'balance',
        'cashFlow', 'trial', 'ledger',
        'salesReport', 'purchaseReport',
        'stockReport', 'customerReport',
        'supplierReport', 'expenseReport',
        'taxReport', 'profitReport',
        'dailySummary', 'topItems', 'topCustomers', 'topSuppliers',
        'yearlySales', 'stockValueReport', 'recalculateStock',
        'itemReport', 'bestSelling', 'mostReturned',
    ],

    dashboard: [
        'view', 'user', 'Order', 'Customer',
        'sales', 'purchase', 'inventory',
        'finance', 'reports', 'analytics',
        'Order.ordered', 'Order.shipped', 'Order.inStore', 'Order.delivered',
        'profile', 'city', 'roles', 'currency', 'product', 'supplier',
        'expense', 'return', 'loanRepayment', 'supplierPayment', 'settings',
    ],

    audit: ['read', 'list', 'export'],
    archive: ['read', 'list', 'restore', 'delete', 'export'],
    settings: ['read', 'update', 'backup', 'restore'],
    tax: ['create', 'read', 'update', 'delete', 'calculate'],
    discount: ['create', 'read', 'update', 'delete', 'apply'],
    period: ['create', 'read', 'update', 'close', 'reopen'],
    bank: ['create', 'read', 'update', 'delete', 'reconcile'],
    cash: ['create', 'read', 'update', 'delete', 'count'],
    import: ['data', 'validate', 'process'],
    export: ['data', 'report', 'excel', 'pdf', 'backup'],
};

/** Every permission name, e.g. `"sell.create"`. */
export const ALL_PERMISSIONS: string[] = Object.entries(PERMISSION_GROUPS).flatMap(
    ([group, actions]) => actions.map(action => `${group}.${action}`)
);

/**
 * The names the UI guards with, grouped by feature so a component reads
 * `PERMS.report.itemReport` instead of a bare string.
 */
export const PERMS = {
    dashboard: {
        view: 'dashboard.view',
        users: 'dashboard.user',
        roles: 'dashboard.roles',
        city: 'dashboard.city',
        currency: 'dashboard.currency',
        customers: 'dashboard.Customer',
        settings: 'dashboard.settings',
    },
    report: {
        dashboard: 'report.dashboard',
        dailySummary: 'report.dailySummary',
        topItems: 'report.topItems',
        topCustomers: 'report.topCustomers',
        topSuppliers: 'report.topSuppliers',
        yearlySales: 'report.yearlySales',
        stockReport: 'report.stockReport',
        stockValueReport: 'report.stockValueReport',
        profitReport: 'report.profitReport',
        itemReport: 'report.itemReport',
        bestSelling: 'report.bestSelling',
        mostReturned: 'report.mostReturned',
        export: 'report.export',
    },
    product: {
        list: 'product.list',
        create: 'product.create',
        update: 'product.update',
        delete: 'product.delete',
        restoreStock: 'product.restoreStock',
    },
    customer: { list: 'customer.list', create: 'customer.create', update: 'customer.update', delete: 'customer.delete' },
    supplier: { list: 'supplier.list', create: 'supplier.create', update: 'supplier.update', delete: 'supplier.delete' },
    currency: { list: 'currency.list', create: 'currency.create', update: 'currency.update', delete: 'currency.delete' },
    city: { list: 'city.list', create: 'city.create', update: 'city.update', delete: 'city.delete' },
    user: { list: 'user.list', create: 'user.create', update: 'user.update', delete: 'user.delete' },
    role: { list: 'role.list', create: 'role.create', update: 'role.update', delete: 'role.delete' },
    sell: { list: 'sell.list', create: 'sell.create', update: 'sell.update', delete: 'sell.delete' },
    purchase: { list: 'purchase.list', create: 'purchase.create', update: 'purchase.update', delete: 'purchase.delete' },
    returnInvoice: { list: 'returnInvoice.list', create: 'returnInvoice.create', update: 'returnInvoice.update', delete: 'returnInvoice.delete' },
    expense: { list: 'expense.list', create: 'expense.create', update: 'expense.update', delete: 'expense.delete' },
    loanRepayment: { list: 'loanRepayment.list', create: 'loanRepayment.create', update: 'loanRepayment.update', delete: 'loanRepayment.delete' },
    supplierPayment: { list: 'supplierPayment.list', create: 'supplierPayment.create', update: 'supplierPayment.update', delete: 'supplierPayment.delete' },
    audit: { list: 'audit.list', read: 'audit.read', export: 'audit.export' },
    archive: {
        list: 'archive.list',
        read: 'archive.read',
        restore: 'archive.restore',
        delete: 'archive.delete',
        export: 'archive.export',
    },
    settings: { read: 'settings.read', update: 'settings.update', backup: 'settings.backup', restore: 'settings.restore' },
    exportData: { excel: 'export.excel', pdf: 'export.pdf', report: 'export.report' },
} as const;

/** Split `"sell.create"` into its group and action. */
export const splitPermission = (name: string): { group: string; action: string } => {
    const index = name.indexOf('.');
    if (index === -1) return { group: name, action: '' };
    return { group: name.slice(0, index), action: name.slice(index + 1) };
};
